import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import log from 'loglevel';
import { useLocation } from 'react-router-dom';
// Virtual module provided by vite-plugin-pwa. In dev (SW disabled) this is a
// no-op stub, so the hook is always safe to call.
import { useRegisterSW } from 'virtual:pwa-register/react';

import { Alert, AlertDescription } from '../Alert/Alert';
import { Button } from '../Button/Button';

import type { UpdateMoment } from './autoUpdatePolicy';
import {
  decideUpdate,
  hasWorkInProgress,
  markAutoReload,
  readLastAutoReloadAt,
} from './autoUpdatePolicy';

import styles from './PwaUpdatePrompt.module.css';

/** How often a long-lived session asks whether a newer build exists. */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Coming back to a tab / resuming the installed PWA also triggers a check, but
 * only when the last one is at least this stale — otherwise every alt-tab would
 * hit the network.
 */
const RESUME_CHECK_THROTTLE_MS = 5 * 60 * 1000;

/** How often a held update re-asks whether the user has paused. */
const WAIT_RECHECK_MS = 2_000;

/**
 * Keeps the dashboard on the current build.
 *
 * We register with `registerType: 'prompt'` (see vite.config.ts) rather than
 * auto-update, so a freshly deployed service worker sits in the waiting state
 * until something activates it. This component is that something, and its
 * central idea is that the decision is *repeated*, not taken once: a build we
 * cannot safely apply the instant we notice it is applied at the next natural
 * break instead of sitting behind a Reload button nobody looks at.
 *
 * The breaks we act on are a client-side navigation and the user simply going
 * quiet. `autoUpdatePolicy` holds the rules, explains what the previous
 * document-age version of them got wrong, and why resuming the app is not a
 * third break despite looking like one.
 *
 * Activating the waiting worker reloads onto the new assets, which also clears
 * the class of stale-lazy-chunk failures that `utils/reloadOnFails` otherwise
 * recovers from.
 */
export function PwaUpdatePrompt(): JSX.Element | null {
  const { pathname } = useLocation();
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | undefined>();
  // 'none' until a waiting worker turns up and we decide what to do with it.
  // Rendering nothing while undecided keeps the prompt from flashing for a
  // frame on the silent path.
  const [decision, setDecision] = useState<'none' | 'wait' | 'prompt'>('none');

  // Text entered since the last natural break. Unlike the previous version of
  // this component, it is cleared on navigation — otherwise typing a password
  // into the sign-in route (which lives in this SPA) permanently disqualified
  // the session that most needs a silent update.
  const hasTypedRef = useRef(false);
  // `-Infinity` so an untouched page reads as "last interaction infinitely
  // long ago", i.e. idle rather than active.
  const lastInteractionRef = useRef(Number.NEGATIVE_INFINITY);
  // Latches once we have asked the worker to take over, so a re-entrant
  // evaluation can't fire a second reload into the first one.
  const applyingRef = useRef(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, swRegistration) {
      log.info('🛰️  Service worker registered:', swUrl);
      setRegistration(swRegistration);
    },
    onRegisterError(error) {
      log.error('Service worker registration failed:', error);
    },
  });

  const evaluate = useCallback(
    (moment: UpdateMoment): void => {
      if (applyingRef.current) {
        return;
      }

      const now = Date.now();
      const next = decideUpdate({
        moment,
        hasWorkInProgress: hasWorkInProgress(hasTypedRef.current),
        msSinceInteraction: now - lastInteractionRef.current,
        lastAutoReloadAt: readLastAutoReloadAt(),
        now,
      });

      if (next !== 'apply') {
        setDecision(next);
        return;
      }

      applyingRef.current = true;
      setDecision('none');
      markAutoReload(now);
      log.info(`🛰️  New build available (${moment}) — applying it without prompting.`);
      // Activates the waiting worker and reloads onto the new assets.
      void updateServiceWorker(true);
    },
    [updateServiceWorker],
  );

  // Track engagement. Entering anything means there is something to lose;
  // pointer and scroll activity only mean the user's hands are on the page, so
  // they defer the reload to the next quiet moment rather than blocking it.
  //
  // `keydown` alone would miss half of the typing: dictation, autocorrect,
  // swipe and other soft-keyboard input, an IME commit, and paste or drag-drop
  // by mouse all reach the field as `input` with no key ever pressed. Listen in
  // the capture phase so a handler in between can't hide the event by stopping
  // propagation.
  useEffect(() => {
    const markTyped = (): void => {
      hasTypedRef.current = true;
      lastInteractionRef.current = Date.now();
    };
    const markInteraction = (): void => {
      lastInteractionRef.current = Date.now();
    };

    const capture = { capture: true } as const;
    const passiveCapture = { capture: true, passive: true } as const;
    window.addEventListener('keydown', markTyped, capture);
    window.addEventListener('input', markTyped, capture);
    window.addEventListener('pointerdown', markInteraction, passiveCapture);
    window.addEventListener('wheel', markInteraction, passiveCapture);
    window.addEventListener('scroll', markInteraction, passiveCapture);

    return () => {
      window.removeEventListener('keydown', markTyped, capture);
      window.removeEventListener('input', markTyped, capture);
      window.removeEventListener('pointerdown', markInteraction, passiveCapture);
      window.removeEventListener('wheel', markInteraction, passiveCapture);
      window.removeEventListener('scroll', markInteraction, passiveCapture);
    };
  }, []);

  // A registration only looks for a new worker on navigation, so an installed
  // PWA or a tab left open for a day would never learn about a deploy. Poll,
  // and check again when the app is brought back to the foreground after a
  // while — for an app that is resumed rather than reopened, that is the only
  // thing resembling a fresh load it ever gets.
  //
  // Both kinds of check feed the same decision; see `UpdateMoment`.
  const lastCheckAtRef = useRef(Date.now());
  useEffect(() => {
    if (!registration) {
      return undefined;
    }

    const check = (): void => {
      lastCheckAtRef.current = Date.now();
      registration.update().catch((error: unknown) => {
        // Offline, or the server is mid-deploy. The next check picks it up.
        log.debug('Service worker update check failed:', error);
      });
    };

    const interval = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      // Re-ask about a build we are already holding. The retry loop below
      // would get to it, but it is a background timer: browsers throttle and
      // freeze those while hidden, so how soon it ticks after a resume is not
      // ours to rely on. Note this is still `detected` — coming back is not
      // itself evidence the user has nothing in hand, which is the whole
      // reason there is no `resumed` moment (see `UpdateMoment`).
      if (needRefresh) {
        evaluate('detected');
      }
      if (Date.now() - lastCheckAtRef.current >= RESUME_CHECK_THROTTLE_MS) {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [registration, needRefresh, evaluate]);

  // A new build turned up. Whether it was the hourly poll or the check a
  // resume fired that found it makes no difference: the decision is about
  // what the user is doing now, and someone who has just resumed and not
  // touched anything already reads as idle.
  useEffect(() => {
    if (needRefresh) {
      evaluate('detected');
    }
  }, [needRefresh, evaluate]);

  // A client-side navigation: the previous screen and anything typed into it
  // are gone either way, so this is the cheapest possible moment to swap builds.
  const previousPathRef = useRef(pathname);
  useEffect(() => {
    if (previousPathRef.current === pathname) {
      return;
    }
    previousPathRef.current = pathname;
    hasTypedRef.current = false;
    if (needRefresh) {
      evaluate('navigated');
    }
  }, [pathname, needRefresh, evaluate]);

  // Held because the user was mid-interaction — keep asking until they pause.
  useEffect(() => {
    if (decision !== 'wait') {
      return undefined;
    }
    const timer = window.setInterval(() => evaluate('detected'), WAIT_RECHECK_MS);
    return () => window.clearInterval(timer);
  }, [decision, evaluate]);

  if (!needRefresh || decision !== 'prompt') {
    return null;
  }

  return (
    <div className={styles.Container} role="status" aria-live="polite">
      <Alert variant="info" className={styles.Alert}>
        <AlertDescription>A new version of Revvly is available.</AlertDescription>
        <div className={styles.Actions}>
          <Button variant="ghost" size="sm" onClick={() => setNeedRefresh(false)}>
            Later
          </Button>
          <Button variant="primary" size="sm" onClick={() => updateServiceWorker(true)}>
            Reload
          </Button>
        </div>
      </Alert>
    </div>
  );
}

export default PwaUpdatePrompt;
